import {
  Box,
  Button,
  Center,
  Grid,
  GridItem,
  HStack,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack
} from '@chakra-ui/react'
import { ArrowUpFull } from '@components/icons'
import { SendIcon } from '@icons'
import React, { useEffect } from 'react'
import { useGrowsurfControllerMarkUpdatesAsSeen } from '~/clinician-api'
import { InviteStatusItemDTOReferralStatus } from '~/clinician-api/models'

interface InviteItem {
  recipientEmail: string
  referralStatus: InviteStatusItemDTOReferralStatus
}

interface InviteStatusData {
  lastSeenInviteStatusCounts?: {
    linkVisitCount?: number
    signupCount?: number
    activatedCount?: number
    paidCount?: number
  }
  inviteStatusCounts?: {
    linkVisitCount?: number
    signupCount?: number
    activatedCount?: number
    paidCount?: number
  }
  invites?: InviteItem[]
}

interface InviteStatusTabProps {
  inviteStatusData?: InviteStatusData
  isLoadingInviteStatus: boolean
  paidCount: number
  onSendInvitesClick: () => void
}

const UpdateIndicator = () => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="20px"
      height="20px"
      borderRadius="full"
      bg="#BFF2D6"
    >
      <ArrowUpFull fill="#0D8443" />
    </Box>
  )
}
export const InviteStatusTab: React.FC<InviteStatusTabProps> = ({
  inviteStatusData,
  isLoadingInviteStatus,
  paidCount,
  onSendInvitesClick
}) => {
  const { mutate: markUpdatesAsSeen } = useGrowsurfControllerMarkUpdatesAsSeen()

  const lastSeenInviteStatusCounts =
    inviteStatusData?.lastSeenInviteStatusCounts
  const inviteStatusCounts = inviteStatusData?.inviteStatusCounts

  const impressionsIncreased =
    (inviteStatusCounts?.linkVisitCount ?? 0) >
    (lastSeenInviteStatusCounts?.linkVisitCount ?? 0)
  const signupsIncreased =
    (inviteStatusCounts?.signupCount ?? 0) >
    (lastSeenInviteStatusCounts?.signupCount ?? 0)
  const activationsIncreased =
    (inviteStatusCounts?.activatedCount ?? 0) >
    (lastSeenInviteStatusCounts?.activatedCount ?? 0)
  const conversionsIncreased =
    (inviteStatusCounts?.paidCount ?? 0) >
    (lastSeenInviteStatusCounts?.paidCount ?? 0)

  useEffect(() => {
    markUpdatesAsSeen()
  }, [])

  return (
    <Box p={4} maxHeight="460px" overflowY="auto">
      <VStack spacing={6} align="stretch">
        <Box textAlign="center">
          <Text fontSize="24px" fontWeight="bold">
            Track your invites
          </Text>
          {/* <Text mt={2}>
            Sent an invite and the recipient hasn't signed up yet?<br />
            Select "Send reminder" and we'll give them a little nudge.
          </Text> */}
        </Box>

        {isLoadingInviteStatus ? (
          <Center p={8}>
            <Spinner size="lg" />
          </Center>
        ) : (
          <>
            <Grid templateColumns="repeat(4, 1fr)" gap={4} mt={4}>
              <GridItem colSpan={1}>
                <Box
                  p={4}
                  bg="#F4F4F4"
                  borderRadius="md"
                  textAlign="left"
                  height="121px"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <HStack>
                    <Text fontSize="24px" fontWeight="bold">
                      {inviteStatusData?.inviteStatusCounts?.linkVisitCount ||
                        0}
                    </Text>
                    {impressionsIncreased && <UpdateIndicator />}
                  </HStack>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    Visited your referral link
                  </Text>
                </Box>
              </GridItem>
              <GridItem colSpan={1}>
                <Box
                  p={4}
                  bg="#F4F4F4"
                  border="1px solid rgba(61, 65, 69, 0.06)"
                  borderRadius="md"
                  textAlign="left"
                  height="121px"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <HStack>
                    <Text fontSize="24px" fontWeight="bold">
                      {inviteStatusData?.inviteStatusCounts?.signupCount || 0}
                    </Text>
                    {signupsIncreased && <UpdateIndicator />}
                  </HStack>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    Created their free account
                  </Text>
                </Box>
              </GridItem>
              <GridItem colSpan={1}>
                <Box
                  p={4}
                  bg="#F4F4F4"
                  borderRadius="md"
                  textAlign="left"
                  height="121px"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <HStack>
                    <Text fontSize="24px" fontWeight="bold">
                      {inviteStatusData?.inviteStatusCounts?.activatedCount ||
                        0}
                    </Text>
                    {activationsIncreased && <UpdateIndicator />}
                  </HStack>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    Actively using free account
                  </Text>
                </Box>
              </GridItem>
              <GridItem colSpan={1}>
                <Box
                  p={4}
                  bg={(paidCount || 0) > 0 ? '#BFF2D6' : '#F4F4F4'}
                  borderRadius="md"
                  textAlign="left"
                  borderColor={
                    (paidCount || 0) > 0 ? '#0D84430F' : 'transparent'
                  }
                  borderWidth={(paidCount || 0) > 0 ? '1px' : '0'}
                  height="121px"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <HStack justifyContent="left">
                    <Text
                      fontSize="24px"
                      fontWeight="bold"
                      color={(paidCount || 0) > 0 ? '#0D8443' : 'inherit'}
                    >
                      {paidCount || 0}
                    </Text>
                    {(paidCount || 0) > 0 ? (
                      <Text fontSize="24px">😀</Text>
                    ) : null}
                    {conversionsIncreased && <UpdateIndicator />}
                  </HStack>
                  <Text
                    fontSize="sm"
                    color={(paidCount || 0) > 0 ? '#0D8443' : 'gray.600'}
                    noOfLines={2}
                  >
                    Became paid subscriber
                  </Text>
                </Box>
              </GridItem>
            </Grid>

            {inviteStatusData?.invites &&
            inviteStatusData.invites.length > 0 ? (
              <Box borderRadius="md" boxShadow="sm">
                <Table variant="simple">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr borderBottom="1px" borderColor="gray.200">
                      <Th py={4} textTransform="none">
                        Recipient
                      </Th>
                      <Th py={4} textTransform="none">
                        Status
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {inviteStatusData?.invites.map((invite, index) => (
                      <Tr key={index} borderBottom="1px" borderColor="gray.200">
                        <Td py={4}>
                          {invite.recipientEmail.replace(
                            /^(.{3}).*@(.{3}).*$/,
                            '$1***@$2***'
                          )}
                        </Td>
                        <Td py={4}>
                          <Box
                            display="inline-block"
                            px={2}
                            py={1}
                            borderRadius="4px"
                            bg={
                              invite.referralStatus ===
                              InviteStatusItemDTOReferralStatus.invite_sent
                                ? '#E4E5E6'
                                : invite.referralStatus ===
                                    InviteStatusItemDTOReferralStatus.signed_up
                                  ? 'rgba(45, 84, 232, 0.15)'
                                  : invite.referralStatus ===
                                      InviteStatusItemDTOReferralStatus.activated
                                    ? 'rgba(45, 84, 232, 0.15)'
                                    : invite.referralStatus ===
                                        InviteStatusItemDTOReferralStatus.paid
                                      ? '#80E5AE80'
                                      : undefined
                            }
                            color={
                              invite.referralStatus ===
                              InviteStatusItemDTOReferralStatus.invite_sent
                                ? '#282828'
                                : invite.referralStatus ===
                                    InviteStatusItemDTOReferralStatus.signed_up
                                  ? '#2D54E8'
                                  : invite.referralStatus ===
                                      InviteStatusItemDTOReferralStatus.activated
                                    ? '#2D54E8'
                                    : invite.referralStatus ===
                                        InviteStatusItemDTOReferralStatus.paid
                                      ? '#0D8443'
                                      : undefined
                            }
                            fontWeight="bold"
                            fontSize="xs"
                          >
                            {invite.referralStatus ===
                            InviteStatusItemDTOReferralStatus.invite_sent
                              ? 'INVITE SENT'
                              : invite.referralStatus ===
                                  InviteStatusItemDTOReferralStatus.signed_up
                                ? 'FREE'
                                : 'FREE ACTIVE'}
                          </Box>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <VStack>
                <Text textAlign="center" maxWidth="390px" mb="32px" mt="16px">
                  It’s a numbers game — the more invites you send, the more
                  likely you’ll save big time!
                </Text>
                <Button
                  as={Link}
                  maxWidth="250px"
                  height="48px"
                  leftIcon={<SendIcon fill="#2D54E9" />}
                  width="100%"
                  variant="outline"
                  borderColor="rgba(55, 60, 65, 0.14)"
                  colorScheme="#2D54E9"
                  bg="rgba(45, 84, 232, 0.10)"
                  color="#2D54E9"
                  borderRadius="full"
                  onClick={onSendInvitesClick}
                  _hover={{
                    textDecoration: 'none',
                    bg: 'rgba(45, 84, 232, 0.15)'
                  }}
                >
                  Send more invites
                </Button>
              </VStack>
            )}
          </>
        )}
      </VStack>
    </Box>
  )
}

export default InviteStatusTab
